// ABOUTME: Security tab in user account settings for MFA management.
// ABOUTME: Shows MFA status with enable/disable controls and recovery code management.
import { Box, useTheme } from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import Subheading from "../../../../components/theme/text/Subheading";
import Text from "../../../../components/theme/text/Text";
import Shbutton from "../../../../components/theme/buttons/Shbutton";
import Icon from "../../../../components/theme/icons/Icon";
import { tokens } from "../../../../theme";
import { useGetMfaStatusQuery, useSetupMfaMutation } from "../../../../newapi/user/mfaSlice";
import MpSetupMfa from "../../../../components/global/ModalsProgression/MpSetupMfa/MpSetupMfa";
import MConfirmDisableMfa from "../../../../components/global/Modals/MConfirmDisableMfa";
import MConfirmRegenerateCodes from "../../../../components/global/Modals/MConfirmRegenerateCodes";
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import LockOpenOutlinedIcon from '@mui/icons-material/LockOpenOutlined';
import KeyOutlinedIcon from '@mui/icons-material/KeyOutlined';
import { showToastMessage } from "../../../../utils/toasts";


const Security = (props) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const { t } = useTranslation();

    const { data: mfaStatus, isLoading: statusLoading } = useGetMfaStatusQuery();
    const [setupMfa] = useSetupMfaMutation();
    const [setupModalOpen, setSetupModalOpen] = useState(false);
    const [setupData, setSetupData] = useState(null);
    const [disableModalOpen, setDisableModalOpen] = useState(false);
    const [regenerateModalOpen, setRegenerateModalOpen] = useState(false);

    const mfaEnabled = mfaStatus?.data?.enabled ?? false;
    const recoveryCodesRemaining = mfaStatus?.data?.recoveryCodesRemaining ?? 0;

    const handleEnableMfa = async () => {
        try {
            const result = await setupMfa().unwrap();
            if (result.success) {
                setSetupData(result.data);
                setSetupModalOpen(true);
            }
        } catch (err) {
            showToastMessage("error", err?.data?.error || "Error setting up MFA");
        }
    };

    const cardSx = {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        background: theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[0],
        border: `1px solid ${theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[100]}`,
        borderRadius: 7,
        height:56,
        boxShadow:theme.palette.mode === "dark" ? null :  `0px 2px 4px ${colors.grey[200]}`,
        p: 1,
    };

    return (
        <Box sx={{ p: 4 }}>
            <Subheading>{t("two_factor_authentication")}</Subheading>
            <Text light sx={{ mt: 0.5, mb: 3 }}>{t("add_extra_security")}</Text>

            {/* Authenticator App Card */}
            <Box sx={cardSx}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Icon  rounded theme={mfaEnabled ? "green" : "grey"}>
                        {mfaEnabled ? <LockOutlinedIcon /> : <LockOpenOutlinedIcon />}
                    </Icon>
                    <Box
                        sx={{
                            display:"flex",
                            flexDirection:'column',
                        }}
                    >
                        <Text bold>{t("authenticator_app")}</Text>
                        <Text light>
                            {mfaEnabled ? t("mfa_active") : t("not_configured")}
                        </Text>
                    </Box>
                </Box>
                {mfaEnabled ? (
                    <Shbutton
                        color="red"
                        variant="contained"
                        onClick={() => setDisableModalOpen(true)}
                    >
                        {t("disable")}
                    </Shbutton>
                ) : (
                    <Shbutton
                        color={theme.palette.mode === "dark" ? "blue" : "primary"}
                        variant="contained"
                        onClick={handleEnableMfa}
                        isLoading={statusLoading}
                    >
                        {t("enable")}
                    </Shbutton>
                )}
            </Box>

            {/* Recovery Codes Card (only when MFA enabled) */}
            {mfaEnabled && (
                <Box sx={{ ...cardSx, mt: 2 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        <Icon  rounded theme="grey">
                            <KeyOutlinedIcon />
                        </Icon>
                        <Box
                            sx={{
                                display:"flex",
                                flexDirection:'column',
                            }}
                        >
                            <Text bold>{t("recovery_codes")}</Text>
                            <Text light>
                                {t("x_of_y_remaining", { remaining: recoveryCodesRemaining, total: 8 })}
                            </Text>
                        </Box>
                    </Box>
                    <Shbutton
                        variant="contained"
                        onClick={() => setRegenerateModalOpen(true)}
                        color={theme.palette.mode === "dark" ? "blue" : "primary"}
                    >
                        {t("regenerate")}
                    </Shbutton>
                </Box>
            )}

            {/* Modals */}
            <MpSetupMfa
                open={setupModalOpen}
                handleClose={() => { setSetupModalOpen(false); setSetupData(null); }}
                setupData={setupData}
            />
            <MConfirmDisableMfa
                open={disableModalOpen}
                handleClose={() => setDisableModalOpen(false)}
            />
            <MConfirmRegenerateCodes
                open={regenerateModalOpen}
                handleClose={() => setRegenerateModalOpen(false)}
            />
        </Box>
    );
};

export default Security;
